/* playTeadsHandler.tsx */
import Container from '@components/atoms/container/adsContainer';
import TeadsErrorBoundary from './teadsErrorBoundary';
import TeadsUtils, { Placement } from '@shared/utils/teads';
import { adsSelector, clearPlayAd } from '@shared/store/slices/ads.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useRef } from 'react';

/**
 * Teads constants
 */
const CONTAINER_ID = 'play-teads-container';
const VIDEO_ID = 'video';

/**
 * PlayTeadsFallback component.
 * Handles the fallback state for the PlayTeadsHandler component.
 */
function PlayTeadsFallback() {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(clearPlayAd());
  }, [dispatch]);

  return null;
}

/**
 * PlayTeadsUI component.
 * Handles the UI for the PlayTeadsHandler component.
 */
function PlayTeadsUI() {
  return (
    <Container transparent>
      <div
        style={{ width: '100%', height: '100%' }}
        id={'play-teads-container'}
      />
    </Container>
  );
}

/**
 * PlayTeadsHandlerContent component.
 * Handles the Teads play ad placement.
 */
function PlayTeadsHandlerContent() {
  const { playAd } = useAppSelector(adsSelector);
  const inplayPlacement = useRef<any>(null);
  const teadsInstance = useRef<any>(null);

  /**
   * Initialize Teads instance and placement
   */
  useEffect(() => {
    if (!window.teads?.SDK) return;
    const { sdkInstance, placementInstance } = TeadsUtils.initializeTeads({
      asset: playAd?.asset,
      adsWindow: playAd?.adsWindow,
      containerId: CONTAINER_ID,
      placementId: parseInt(playAd?.placementId ?? ''),
      placementType: Placement.IN_PLAY,
      videoId: VIDEO_ID,
    });
    inplayPlacement.current = placementInstance;
    teadsInstance.current = sdkInstance;
    return () => {
      sdkInstance?.destroy?.();
    };
  }, []);

  /**
   * Update content metadata and play ad
   */
  useEffect(() => {
    if (!teadsInstance.current) return;
    if (!inplayPlacement.current) return;
    // Play ad
    if (playAd) {
      // Update content metadata
      const metadata = TeadsUtils.buildContentMetadata(playAd?.asset);
      teadsInstance.current?.setContentMetaData(metadata);
      // Load and play ad
      if (!inplayPlacement.current?.isAdReady) {
        inplayPlacement.current.loadAd();
      } else {
        inplayPlacement.current.playAd();
      }
    }
    // Stop ad
    else {
      inplayPlacement.current?.stopAd();
    }
  }, [playAd?.asset]);

  /**
   * Return the component
   */
  return <PlayTeadsUI />;
}

/**
 * PlayTeadsHandler main component.
 * Wraps the PlayTeadsHandlerContent component in a TeadsErrorBoundary component.
 */
export default function PlayTeadsHandler() {
  return (
    <TeadsErrorBoundary fallback={<PlayTeadsFallback />}>
      <PlayTeadsHandlerContent />
    </TeadsErrorBoundary>
  );
}
